import { FC, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { PardotFormHandlers } from 'constants/constants'
import { useForm } from 'react-hook-form'
import { StayInformedFields } from './types'
import classNames from 'classnames'
import LoadingDots from 'components/loading-dots/LoadingDots'
import styles from './StayInformed.module.scss'
import { subscribeError } from 'store/app/actions/subscribe'
import { updateSimulationInput } from 'store/simulation/actions/updateSimulationInput'
import { SimulationInputKeys } from 'store/simulation/types'
import { updateRegistrationInput } from 'store/registration/actions/updateRegistrationInputs'
import { InputGroupKeys, PersonalDataFieldKeys } from 'store/registration/types'
import { subscribePardotProspect } from 'api/customerFlow'
import { useRouter } from 'next/router'
import Button from 'components/button/Button'
import { hasNoNumber } from 'utils/helpers'

export const StayInformed: FC = () => {
  // REDUX STORE
  const dispatch = useDispatch()

  // Next router
  const { locale } = useRouter()

  // i18n
  const { t } = useTranslation(['home', 'validation'])

  // React hook form
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<StayInformedFields>({ mode: 'onChange' })

  // Local state
  const [loading, setLoading] = useState<boolean>(false)
  const [sent, setSent] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)

  /**
   * Handles the submit after validation
   *
   * @param data
   */
  const onSubmit = async (data: StayInformedFields) => {
    setSent(false)
    setError(false)
    setLoading(true)

    try {
      const { firstName, lastName, email, permission } = data

      // Update the fields in the simulation state
      dispatch(updateSimulationInput({ key: SimulationInputKeys.FIRSTNAME, value: firstName }))
      dispatch(updateSimulationInput({ key: SimulationInputKeys.LASTNAME, value: lastName }))
      dispatch(updateSimulationInput({ key: SimulationInputKeys.EMAIL, value: email }))
      dispatch(updateSimulationInput({ key: SimulationInputKeys.PERMISSION, value: permission }))

      // Update the fields in the registration state (firstname, lastname & email are also used there)
      dispatch(updateRegistrationInput({ group: InputGroupKeys.PERSONAL_DATA, key: PersonalDataFieldKeys.FIRSTNAME, value: firstName }))
      dispatch(updateRegistrationInput({ group: InputGroupKeys.PERSONAL_DATA, key: PersonalDataFieldKeys.LASTNAME, value: lastName }))
      dispatch(updateRegistrationInput({ group: InputGroupKeys.PERSONAL_DATA, key: PersonalDataFieldKeys.EMAIL, value: email }))

      // Subscribe the prospect to Pardot
      await subscribePardotProspect(PardotFormHandlers.NEWSLETTER, {
        firstName,
        lastName,
        email,
        language: locale,
        emailOptIn: 'true'
      })

      setSent(true)
    } catch (e) {
      setError(true)
      dispatch(subscribeError())
    }

    setLoading(false)
  }

  return (
    <form className={styles['stay-informed']} onSubmit={handleSubmit(onSubmit)}>
      <div className={classNames('form-group', styles['form-group'])}>
        <div className={classNames('form-input', styles['form-input'])}>
          <label
            htmlFor="firstName"
            className={classNames('label margin-bottom-xs', {
              'is-red': errors.firstName && errors.firstName.type === 'required'
            })}
          >
            {t('firstName')}
          </label>
          <input
            id="firstName"
            type="text"
            className={classNames(styles.input, 'input', 'margin-bottom-m')}
            {...register('firstName', {
              required: true,
              minLength: 2,
              maxLength: 40,
              validate: (value: string) => hasNoNumber(value)
            })}
          />
          {errors && errors.firstName && (
            <span className="is-block margin-bottom-m is-red">
              {errors.firstName.type === 'required' && t('validation:required')}
              {errors.firstName.type === 'minLength' &&
                t('validation:min.string', {
                  attribute: t('commonFormFields:firstName', 'Voornaam'),
                  length: 2
                })}
              {errors.firstName.type === 'maxLength' &&
                t('validation:max.string', {
                  attribute: t('commonFormFields:firstName', 'Voornaam'),
                  length: 40
                })}
              {errors.firstName.type === 'validate' &&
                t('validation:noNumbers', {
                  attribute: t('commonFormFields:firstName')
                })}
            </span>
          )}
        </div>

        <div className={classNames('form-input', styles['form-input'])}>
          <label
            htmlFor="lastName"
            className={classNames('label margin-bottom-xs', {
              'is-red': errors.lastName && errors.lastName.type === 'required'
            })}
          >
            {t('lastName')}
          </label>
          <input
            id="lastName"
            type="text"
            className={classNames(styles.input, 'input', 'margin-bottom-m')}
            {...register('lastName', {
              required: true,
              maxLength: 80,
              validate: (value: string) => hasNoNumber(value)
            })}
          />
          {errors && errors.lastName && (
            <span className="is-block margin-bottom-m is-red">
              {errors.lastName.type === 'required' && t('validation:required')}
              {errors.lastName.type === 'maxLength' &&
                t('validation:max.string', {
                  attribute: t('commonFormFields:lastName', 'Achternaam'),
                  length: 80
                })}
              {errors.lastName.type === 'validate' &&
                t('validation:noNumbers', {
                  attribute: t('commonFormFields:lastName')
                })}
            </span>
          )}
        </div>
      </div>

      <div className={classNames('form-input', styles['form-input'])}>
        <label
          htmlFor="email"
          className={classNames('label margin-bottom-xs', {
            'is-red': errors.email && errors.email.type === 'required'
          })}
        >
          {t('email')}
        </label>
        <input
          id="email"
          type="email"
          className={classNames(styles.input, 'input', 'margin-bottom-m')}
          {...register('email', { required: true })}
        />
      </div>

      <label
        className={classNames('margin-bottom-m', {
          'is-red': errors.permission && errors.permission.type === 'required'
        })}
      >
        <input type="checkbox" {...register('permission', { required: true })} />
        &nbsp;
        {t('subscribePermission', 'Ik geef Bolt toestemming om mij e-mails te sturen.')}
      </label>

      <Button isDisabled={loading || sent || error} isFullWidthMobile isInverse withGradient>
        {loading ? <LoadingDots /> : t('subscribeYourself', 'Schrijf je in')}
      </Button>

      {sent && (
        <p className="success margin-top-m margin-bottom-none">
          {t('subscribedSuccess', 'Bedankt voor het aanmelden voor onze nieuwsbrief!')}
        </p>
      )}

      {error && (
        <p className="error is-red margin-top-m margin-bottom-none">
          {t('subscribedFailed', 'Er ging iets mis, probeer het later opnieuw.')}
        </p>
      )}
    </form>
  )
}

export default StayInformed
